/**
 * Connection Testing Utility
 * Run this to verify all integrations are working
 */

import { getSupabase } from '../lib/supabase';
import { getPinataClient } from '../lib/ipfs';

/**
 * Test Supabase connection
 */
export const testSupabase = async () => {
  console.log('🔍 Testing Supabase connection...');
  
  try {
    const supabase = getSupabase();
    
    // Test 1: Check if client is real (not mock)
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl || supabaseUrl.includes('your') || supabaseUrl.includes('xxx')) {
      console.error('❌ Supabase URL not configured');
      return { success: false, error: 'Supabase URL not set' };
    }
    
    // Test 2: Try to query a table (this will fail if not connected, but that's ok)
    const { data, error } = await supabase
      .from('profiles')
      .select('id')
      .limit(1);
    
    if (error && error.code !== 'PGRST116') {
      // PGRST116 is "not found" which is fine for empty table
      console.error('❌ Supabase connection error:', error);
      return { success: false, error: error.message };
    }
    
    console.log('✅ Supabase connection successful');
    return { success: true, data };
  } catch (error) {
    console.error('❌ Supabase test failed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Test Pinata connection
 */
export const testPinata = async () => {
  console.log('🔍 Testing Pinata connection...');
  
  try {
    const pinata = getPinataClient();
    
    if (!pinata) {
      const jwtToken = import.meta.env.VITE_PINATA_JWT_TOKEN;
      const apiKey = import.meta.env.VITE_PINATA_API_KEY;
      
      if (!jwtToken && !apiKey) {
        console.error('❌ Pinata credentials not configured');
        return { success: false, error: 'Pinata credentials not set' };
      }
      
      console.error('❌ Pinata client creation failed');
      return { success: false, error: 'Failed to create Pinata client' };
    }
    
    // Test: Try a lightweight operation (get user info)
    try {
      // Try to get user data - this will fail if auth is invalid
      const userData = await pinata.userData();
      console.log('✅ Pinata connection successful');
      return { success: true, data: userData };
    } catch (error) {
      // If this fails, credentials might be wrong
      const errorMsg = error.message || error.toString();
      console.error('❌ Pinata authentication failed:', errorMsg);
      
      // Provide helpful error messages
      if (errorMsg.includes('401') || errorMsg.includes('Unauthorized')) {
        return { success: false, error: 'Pinata authentication failed - check your API key/JWT token' };
      } else if (errorMsg.includes('403') || errorMsg.includes('Forbidden')) {
        return { success: false, error: 'Pinata API key lacks required permissions' };
      } else {
        return { success: false, error: `Pinata error: ${errorMsg}` };
      }
    }
  } catch (error) {
    console.error('❌ Pinata test failed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Test Reown AppKit configuration
 */
export const testReown = () => {
  console.log('🔍 Testing Reown AppKit configuration...');
  
  try {
    const projectId = import.meta.env.VITE_REOWN_PROJECT_ID;
    
    if (!projectId || projectId === 'YOUR_PROJECT_ID') {
      console.error('❌ Reown Project ID not configured');
      return { success: false, error: 'Reown Project ID not set' };
    }
    
    if (projectId.length < 20) {
      console.warn('⚠️ Reown Project ID seems invalid (too short)');
    }
    
    console.log('✅ Reown AppKit configured');
    return { success: true, projectId };
  } catch (error) {
    console.error('❌ Reown test failed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Run all connection tests
 */
export const testAllConnections = async () => {
  console.log('🚀 Starting connection tests...\n');
  
  const results = {
    supabase: await testSupabase(),
    pinata: await testPinata(),
    reown: testReown(),
  };
  
  console.log('\n📊 Test Results:');
  console.log('================');
  console.log(`Supabase: ${results.supabase.success ? '✅' : '❌'}`);
  console.log(`Pinata: ${results.pinata.success ? '✅' : '❌'}`);
  console.log(`Reown: ${results.reown.success ? '✅' : '❌'}`);
  
  const allPassed = Object.values(results).every(r => r.success);
  
  if (allPassed) {
    console.log('\n🎉 All connections successful!');
  } else {
    console.log('\n⚠️ Some connections failed. Check errors above.');
  }
  
  return results;
};

